import { ArrowLeft, Power, RotateCw, SkipForward } from "lucide-react";
import { Link, Outlet, useOutletContext, useParams } from "react-router-dom";

import { useFeed, useFeedHealthState, useNowPlaying } from "../../api/hooks.js";
import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { TonePill } from "../../design-system/feed-state.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { Tabs } from "../../design-system/tabs.js";
import { buildConsoleRow } from "./console-model.js";
import { useFeedActions } from "./use-feed-actions.js";
import type { Feed, FeedHealth, NowPlaying } from "../../api/types.js";

/**
 * One feed, in full.
 *
 * The header is the same three facts the console row carries — what it is, how it is,
 * and what can be done to it — so that arriving here from a row is a change of depth
 * and not a change of subject. Everything below it is a face of the same feed at its
 * own address, which is what makes a configuration screen linkable and reloadable.
 */
export interface FeedContext {
  feed: Feed;
  live: NowPlaying | null;
  health: FeedHealth | null;
  reload: () => void;
}

export function useFeedContext(): FeedContext {
  return useOutletContext<FeedContext>();
}

export function FeedDetailLayout() {
  const { feedId = "" } = useParams();
  const feed = useFeed(feedId, true);
  const live = useNowPlaying(feedId);
  const health = useFeedHealthState(feedId);
  const row = feed.data
    ? buildConsoleRow({
        feed: feed.data,
        live: live.data,
        liveLoading: live.loading,
        liveError: live.error,
        health: health.data,
      })
    : null;
  const actions = useFeedActions({ id: feedId }, feed.reload);

  if (feed.loading) {
    return (
      <div className="flex flex-col gap-6">
        <BackLink />
        <StateSurface kind="loading" title="Reading this feed" />
      </div>
    );
  }

  if (!row || !feed.data) {
    return (
      <div className="flex flex-col gap-6">
        <BackLink />
        <StateSurface
          action={
            <Button onClick={feed.reload} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="This feed could not be read"
        >
          {feed.error ?? "No feed answers at this address."}
        </StateSurface>
      </div>
    );
  }

  const record = feed.data;

  return (
    <div className="flex flex-col gap-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-ink lg:text-[1.75rem]">
            {record.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <TonePill label={row.status.label} pulse tone={row.status.tone} />
            <span
              className={cn(
                "min-w-0 text-xs",
                row.status.tone === "err"
                  ? "text-err"
                  : row.status.tone === "warn"
                    ? "text-warn"
                    : "text-ink-3",
              )}
            >
              {row.status.detail}
            </span>
          </div>
          {record.description ? (
            <p className="mt-2 max-w-2xl text-sm leading-6 text-ink-3">
              {record.description}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={!record.enabled || actions.busy === "skip"}
            onClick={() => {
              actions.run("skip");
            }}
            size="small"
            variant="secondary"
          >
            <SkipForward aria-hidden="true" />
            Skip
          </Button>
          <Button
            disabled={!record.enabled || actions.busy === "restart"}
            onClick={() => {
              actions.run("restart");
            }}
            size="small"
            variant="secondary"
          >
            <RotateCw aria-hidden="true" />
            Restart
          </Button>
          <Button
            disabled={actions.busy === "enable" || actions.busy === "disable"}
            onClick={() => {
              actions.run(record.enabled ? "disable" : "enable");
            }}
            size="small"
            variant="secondary"
          >
            <Power aria-hidden="true" />
            {record.enabled ? "Disable" : "Enable"}
          </Button>
        </div>
      </header>

      {actions.outcome ? (
        <button
          className={cn(
            "-mt-2 w-fit text-left text-xs",
            actions.outcome.tone === "ok" && "text-ink-3",
            actions.outcome.tone === "warn" && "text-warn",
            actions.outcome.tone === "err" && "text-err",
          )}
          onClick={actions.dismiss}
          type="button"
        >
          {actions.outcome.text}
        </button>
      ) : null}

      <Tabs
        items={[
          { to: `/feeds/${feedId}`, label: "Overview" },
          { to: `/feeds/${feedId}/outputs`, label: "Outputs" },
          { to: `/feeds/${feedId}/settings`, label: "Settings" },
        ]}
      />

      <Outlet
        context={
          {
            feed: record,
            live: live.data,
            health: health.data,
            reload: feed.reload,
          } satisfies FeedContext
        }
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      className="inline-flex w-fit items-center gap-2 text-sm text-ink-3 transition-colors hover:text-ink"
      to="/feeds"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Feeds
    </Link>
  );
}
